import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useRef } from 'react';
import { type ClusterData } from '../../api/studentNetworkV2Api.types';
import { STUDENT_NETWORK_MAP_ID } from '../../constants/mapConstants';

const MIN_SCALE_SIZE = 12;
const MAX_SCALE_SIZE = 40;
const MIN_STROKE_SIZE = 2;
const MAX_STROKE_SIZE = 4;
const MIN_COUNT = 10;
const MAX_COUNT = 300;

function rescale(x: number, minCutoff: number, maxCutoff: number) {
    return Math.min(1, Math.max(0, x - minCutoff) / (maxCutoff - minCutoff));
}

function lerp(min: number, max: number, t: number, easingFn?: (k: number) => number) {
    const easedT = easingFn ? easingFn(t) : t;
    return easedT * max + (1 - easedT) * min;
}

function sineEase(k: number) {
    return Math.sin((Math.PI / 2) * k);
}

function getClusterStyle(count: number) {
    const t = rescale(count, MIN_COUNT, MAX_COUNT);
    const scale = Math.round(lerp(MIN_SCALE_SIZE, MAX_SCALE_SIZE, t, sineEase));
    const strokeWidth = Math.round(lerp(MIN_STROKE_SIZE, MAX_STROKE_SIZE, t));
    return { diameter: 2 * scale, strokeWidth, zIndex: Math.round(lerp(100, 1, rescale(count, 1, 100))) };
}

// Resolve brand colors at runtime from HeroUI CSS variables so they
// adapt to branding (Quantic coral vs Valar blue). The variables are
// set on the .hero container by the HeroUI theme plugin.
function resolveHeroColor(varName: string, fallback: string): string {
    const hero = document.querySelector('.hero');
    if (!hero) return fallback;
    const hsl = getComputedStyle(hero).getPropertyValue(varName).trim();
    return hsl ? `hsl(${hsl})` : fallback;
}

function getBrandColors() {
    return {
        primary: resolveHeroColor('--heroui-primary', 'hsl(352, 100%, 65%)'),
        muted: resolveHeroColor('--heroui-primary-200', 'hsl(352, 100%, 85%)'),
    };
}

function buildDotHtml(count: number, diameter: number, strokeWidth: number, muted: boolean, fadeIn: boolean) {
    const colors = getBrandColors();
    const bg = muted ? colors.muted : colors.primary;
    const fontSize = diameter < 30 ? 9 : 12;
    return `<div style="
        width:${diameter}px;height:${diameter}px;
        border:${strokeWidth}px solid #fff;border-radius:50%;
        font-size:${fontSize}px;font-weight:500;
        display:flex;align-items:center;justify-content:center;
        background:${bg};color:#fff;
        transform:translate(-50%,-50%);
        box-sizing:content-box;cursor:pointer;
        will-change:background-color;
        transition:background-color 0.33s;
        ${fadeIn ? 'animation:cluster-fade-in 0.5s both;' : ''}
    ">${count}</div>`;
}

function positionDots(container: HTMLDivElement, projection: google.maps.MapCanvasProjection) {
    container.querySelectorAll<HTMLElement>('[data-cluster-id]').forEach(el => {
        const lat = Number(el.dataset.lat);
        const lng = Number(el.dataset.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        try {
            const pos = projection.fromLatLngToDivPixel(new google.maps.LatLng(lat, lng));
            if (pos) el.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
        } catch {
            // Skip invalid coordinates
        }
    });
}

type ClusterOverlayProps = {
    clusters: ClusterData[];
    onClick?: (cluster: ClusterData) => void;
    visible?: boolean;
    selectedClusterLatLng?: { lat: number; lng: number } | null;
};

// Max z-index used for count-based ordering (small clusters on top)
const MAX_BASE_ZINDEX = 100;
// z-index for the selected cluster — just above all other markers
const SELECTED_ZINDEX = MAX_BASE_ZINDEX + 1;

// Inject keyframes for the fade-in animation once
let styleInjected = false;
function injectClusterStyles() {
    if (styleInjected) return;
    styleInjected = true;
    const style = document.createElement('style');
    style.textContent = '@keyframes cluster-fade-in{from{opacity:0}to{opacity:1}}';
    document.head.appendChild(style);
}

// oxlint-disable-next-line max-lines-per-function -- single overlay managing all cluster dots
export function ClusterOverlay({ clusters, onClick, visible = true, selectedClusterLatLng }: ClusterOverlayProps) {
    const map = useMap(STUDENT_NETWORK_MAP_ID);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);
    const prevClusterIdsRef = useRef<Set<number>>(new Set());

    // Create overlay once — stays attached for the component's lifetime.
    // draw() repositions dots on every pan/zoom.
    useEffect(() => {
        if (!map) return undefined;
        injectClusterStyles();

        const container = document.createElement('div');
        container.style.cssText = 'position:absolute;top:0;left:0';
        containerRef.current = container;

        const overlay = new google.maps.OverlayView();
        overlay.onAdd = () => overlay.getPanes()?.overlayMouseTarget.appendChild(container);
        overlay.draw = () => {
            const projection = overlay.getProjection();
            if (projection) positionDots(container, projection);
        };
        overlay.onRemove = () => container.remove();
        overlay.setMap(map);
        overlayRef.current = overlay;

        return () => {
            overlay.setMap(null);
            overlayRef.current = null;
            containerRef.current = null;
        };
    }, [map]);

    // Rebuild dots when cluster data changes (new viewport, new filters, etc.)
    useEffect(() => {
        const container = containerRef.current;
        const overlay = overlayRef.current;
        if (!container) return;

        const prevIds = prevClusterIdsRef.current;
        const nextIds = new Set(clusters.map(c => c.id));

        container.innerHTML = '';
        clusters.forEach(cluster => {
            const style = getClusterStyle(cluster.count);
            const isMuted =
                !!selectedClusterLatLng &&
                !(cluster.lat === selectedClusterLatLng.lat && cluster.lng === selectedClusterLatLng.lng);
            const isSelected =
                !!selectedClusterLatLng &&
                cluster.lat === selectedClusterLatLng.lat &&
                cluster.lng === selectedClusterLatLng.lng;
            const zIndex = isSelected ? SELECTED_ZINDEX : style.zIndex;
            const isNew = !prevIds.has(cluster.id);
            const wrapper = document.createElement('div');
            wrapper.dataset.clusterId = String(cluster.id);
            wrapper.dataset.lat = String(cluster.lat);
            wrapper.dataset.lng = String(cluster.lng);
            wrapper.style.cssText = `position:absolute;left:0;top:0;z-index:${zIndex};will-change:transform`;
            wrapper.innerHTML = buildDotHtml(cluster.count, style.diameter, style.strokeWidth, isMuted, isNew);
            if (onClick) wrapper.addEventListener('click', () => onClick(cluster));
            container.appendChild(wrapper);
        });

        prevClusterIdsRef.current = nextIds;
        const projection = overlay?.getProjection();
        if (projection) positionDots(container, projection);
        // oxlint-disable-next-line react-hooks/exhaustive-deps -- selection changes handled separately below
    }, [clusters, onClick]);

    // Update muted/selected state in-place so CSS transitions work
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        container.querySelectorAll<HTMLElement>('[data-cluster-id]').forEach(wrapper => {
            const lat = Number(wrapper.dataset.lat);
            const lng = Number(wrapper.dataset.lng);
            const isSelected =
                !!selectedClusterLatLng && lat === selectedClusterLatLng.lat && lng === selectedClusterLatLng.lng;
            const isMuted = !!selectedClusterLatLng && !isSelected;

            // Update z-index: selected cluster sits just above others
            const count = Number(wrapper.querySelector('div')?.textContent ?? 0);
            const baseZIndex = getClusterStyle(count).zIndex;
            wrapper.style.zIndex = String(isSelected ? SELECTED_ZINDEX : baseZIndex);

            // Update background color in-place so the CSS transition animates
            const dot = wrapper.querySelector<HTMLElement>('div');
            if (dot) {
                const colors = getBrandColors();
                dot.style.background = isMuted ? colors.muted : colors.primary;
            }
        });
    }, [selectedClusterLatLng]);

    // Toggle container opacity for visibility
    useEffect(() => {
        if (!containerRef.current) return;
        containerRef.current.style.opacity = visible ? '1' : '0';
        containerRef.current.style.pointerEvents = visible ? '' : 'none';
    }, [visible]);

    return null;
}
